import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";
import {
	nexcaliberLogo,
	registrationPageImageA,
	registrationPageImageB,
	registrationSuccessImage,
	statementOfUnderstandingPDF
} from "../../../../assets";
import { api } from "../../../../utils/api";
import validateUserName from "../../../../utils/commonFunctions/validateUserName";
import { Link, useNavigate } from "react-router-dom";
// import { Button } from "@material-ui/core";
import ROLES from "../../../../constants/roles";
import { trackPromise } from "react-promise-tracker";
import validatePassword from "../../../../utils/commonFunctions/validatePassword";
import { Validation } from "../../../../@types/validation.types";

// icons and images
import {
	FaEnvelope, // filled envelop
	FaRegEnvelope, // hollowEnvelope
	FaUser, // filled user
	FaRegUser, // hollow user
	FaCheckCircle, // filled check
	FaRegCheckCircle // hollow check
} from "react-icons/fa";

import { FiArrowRight, FiArrowLeft } from "react-icons/fi";
import PersonIcon from "@material-ui/icons/Person";
import { BsPersonBadge, BsPersonBadgeFill } from "react-icons/bs";

// stepper components
import RoleSelection from "./roleSelection/RoleSelection";
import EmailVerification from "./emailVerification/EmailVerification";
import PersonalDetails from "./personalDetails/PersonalDetails";
import LoginDetails from "./loginDetails/LoginDetails";

import "./signUp.scss";
import { UerRegistration } from "../../../../@types/components/signupV2.types";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { Timer } from "../../../../@types/timer.types";
import SuccessPage from "./successPage/SuccessPage";
import { ADMIN_ENV } from "../../../../config/config";
import { Button, Box, Modal } from "@mui/material";

const SignUp = () => {
	const [activeStep, setActiveStep] = useState(1);
	const [complete, setComplete] = useState(false);
	const [OTP, setOTP] = useState("");
	const [timer, setTimer] = useState<Timer>({
		minutes: 1,
		seconds: 59
	});
	const ref = useRef<any>(null);
	const [resendOTPButtonVisible, setResendOTPButtonVisible] = useState(true);
	const [resentOTPCaptionVisible, setResentOTPCaptionVisible] = useState(false);
	const [startTimer, setStartTimer] = useState(false);
	const [OTPVerified, setOTPVerified] = useState(false);
	const [countDownTime, setCountDownTime] = useState<number | null>(null);
	const [verificationToken, setVerificationToken] = useState(null);
	const [confirmPassword, setConfirmPassword] = useState("");
	const [statementOfUnderstanding, setStatementOfUnderstanding] = useState("");
	const [reEnterSSN, setReEnterSSN] = useState("");
	const [registrationHelp, setRegistrationHelp] = useState("");
	const [userNameExists, setUserNameExists] = useState(false);
	const [checkInvalidUserName, setCheckInvalidUserName] = useState(false);
	const [emailExists, setEmailExists] = useState(false);
	const [pasted, setPasted] = useState(false);
	const [hasReadStatementOfUnderstanding, setHasReadStatementOfUnderstanding] = useState(false);
	const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null);
	const [documentDialogProps, setDocumentDialogProps] = useState({
		openDialog: false,
		title: "",
		content: "",
		isParsed: false,
		handleCloseCallback: () =>
			setDocumentDialogProps(
				Object.assign({}, documentDialogProps, {
					openDialog: false
				})
			)
	});
	const [signUpDialogProps, setSignUpDialogProps] = useState({
		openDialog: false,
		title: "",
		content: "",
		actions: []
	});
	const [videoPlayerModalProps, setVideoPlayerModalProps] = useState({
		openModal: false,
		src: "https://media.w3.org/2010/05/sintel/trailer_hd.mp4",
		handleCloseModal: () =>
			setVideoPlayerModalProps(
				Object.assign({}, videoPlayerModalProps, {
					openModal: false
				})
			)
	});
	const [user, setUser] = useState<UerRegistration>({
		first_name: "",
		middle_name: "",
		last_name: "",
		email: "",
		role: "",
		date_of_birth: "",
		SSN: "",
		user_name: "",
		password: ""
	});

	const [validation, setValidation] = useState<Validation>({
		user: {},
		reEnterSSN: "",
		confirmPassword: "",
		status: "invalid"
	});

	const navigate = useNavigate();
	const _validation = useRef<Validation>();

	const stepperItems = [
		{
			stepperIcon: <BsPersonBadge className="stepper-icon" />,
			stepperIconActive: <BsPersonBadgeFill className="stepper-icon" />,
			stepperTextPrimary: "STEP 01",
			stepperTextSecondary: "Role Selection"
		},
		{
			stepperIcon: <FaRegEnvelope className="stepper-icon" />,
			stepperIconActive: <FaEnvelope className="stepper-icon" />,
			stepperTextPrimary: "STEP 02",
			stepperTextSecondary: "Email Verification"
		},
		{
			stepperIcon: <FaRegUser className="stepper-icon" />,
			stepperIconActive: <FaUser className="stepper-icon" />,
			stepperTextPrimary: "STEP 03",
			stepperTextSecondary: "Personal Details"
		},
		{
			stepperIcon: <FaRegCheckCircle className="stepper-icon" />,
			stepperIconActive: <FaCheckCircle className="stepper-icon" />,
			stepperTextPrimary: "STEP 04",
			stepperTextSecondary: "Login Details"
		}
	];

	// =========================================== VALIDATIONS ===============================================

	const handleRoleValidation = useCallback(async () => {
		const { role } = user;
		_validation.current = {
			...validation,
			user: {},
			reEnterSSN: "",
			confirmPassword: "",
			status: "invalid"
		};
		let flag = true;

		if (role.length === 0) {
			_validation.current.user["role"] = "Role is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [user, validation]);

	const handlePersonalDetailsValidation = useCallback(async () => {
		const { role, first_name, last_name, date_of_birth, SSN } = user;
		_validation.current = {
			...validation,
			user: {},
			reEnterSSN: "",
			confirmPassword: "",
			status: "invalid"
		};
		let flag = true;

		if (first_name.length === 0) {
			_validation.current.user["first_name"] = "First Name is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (last_name.length === 0) {
			_validation.current.user["last_name"] = "Last Name is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (role === ROLES.employee || role === ROLES.dependent) {
			if (date_of_birth !== "") {
				if (date_of_birth.length === 0) {
					_validation.current.user["date_of_birth"] = "Date of Birth is required";
					_validation.current["status"] = "invalid";
					flag = false;
				}
			}
			if (String(date_of_birth).length === 0) {
				_validation.current.user["date_of_birth"] = "Date of Birth is required";
				_validation.current["status"] = "invalid";
				flag = false;
			}
			if (SSN.length === 0) {
				_validation.current.user["SSN"] = "SSN is required";
				_validation.current["status"] = "invalid";
				flag = false;
			}
			if (SSN !== null) {
				if (SSN !== "") {
					const _SSN = SSN.replaceAll("-", "");
					if (String(_SSN).length !== 9) {
						_validation.current.user["SSN"] = "Enter correct 9 digit SSN";
						_validation.current["status"] = "invalid";
						flag = false;
					}
				}
			}
			if (SSN !== "") {
				const _SSN = SSN.replaceAll("-", "");
				if (String(_SSN).length !== 9) {
					_validation.current.user["SSN"] = "Enter correct 9 digit SSN";
					_validation.current["status"] = "invalid";
					flag = false;
				}
			}
			if (SSN !== "") {
				if (reEnterSSN === "") {
					_validation.current["reEnterSSN"] = "Re-Enter SSN cannot be Empty, as SSN is Given";
					_validation.current["status"] = "invalid";
					flag = false;
				}
			}
			if (SSN !== "") {
				const _SSN = SSN.replaceAll("-", "");
				const _reEnterSSN = reEnterSSN.replaceAll("-", "");
				if (_SSN !== _reEnterSSN) {
					_validation.current["reEnterSSN"] = "Must be same as SSN";
					_validation.current["status"] = "invalid";
					flag = false;
				}
			}
			if (reEnterSSN !== "") {
				if (SSN === "") {
					_validation.current.user["SSN"] = "SSN cannot be Empty, as Re-Enter SSN is Given";
					_validation.current["status"] = "invalid";
					flag = false;
				}
			}
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reEnterSSN, user, validation]);

	const handlecredentialValidation = useCallback(async () => {
		const { user_name, password } = user;
		_validation.current = {
			...validation,
			user: {},
			reEnterSSN: "",
			confirmPassword: "",
			status: "invalid"
		};
		let flag = true,
			confirmPasswordFlag = true,
			validPasswordFlag = true;

		if (user_name.length === 0) {
			_validation.current.user["user_name"] = "User Name is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (password.length === 0) {
			_validation.current.user["password"] = "Password is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (password.length !== 0) {
			if (!validatePassword(password)) {
				_validation.current.user["password"] = "Please enter a valid password";
				_validation.current["status"] = "invalid";
				validPasswordFlag = false;
			}
		}
		if (confirmPassword !== password) {
			_validation.current["confirmPassword"] = "Password doesn't match";
			_validation.current["status"] = "invalid";
			confirmPasswordFlag = false;
		}

		if (flag === true && confirmPasswordFlag === true && validPasswordFlag === true)
			_validation.current["status"] = "valid";
		else if (confirmPasswordFlag === false) {
			_validation.current["status"] = "invalid";
			alert("Confirm New Password must be same with New Password!");
		} else if (validPasswordFlag === false) {
			_validation.current["status"] = "invalid";
			alert(
				"Password length must be at least 8 characters and contain at least one lowercase letter, one uppercase letter, one number and one special character!"
			);
		} else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [confirmPassword, user, validation]);

	// =========================================== HANDLE CHANGE ===============================================

	const handleDateChange = useCallback(
		(date: MaterialUiPickersDate) => {
			const _date = new Date(date as any) as unknown as string;
			const _user = {
				...user,
				date_of_birth: _date
			};
			setUser(Object.assign({}, _user));
		},
		[setUser, user]
	);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		const { currentTarget } = event;
		setAnchorEl(currentTarget);
	};

	const handleOTPChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setOTP(value);
	};

	// OTP VERIFICATION  ALERT

	const isMobileDevice = () => {
		return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
	};

	const handleSendOTPToEmailClick = async () => {
		const payload = {
			email: user.email,
			type: "VERIFICATION"
		};
		const _verificationToken = await api.OTP.sendToEmail(payload);
		if (_verificationToken) {
			setOTPVerified(false);

			if (isMobileDevice()) {
				// console.log("mobile device");
				if ("Notification" in window) {
					if (Notification.permission === "denied" || "default") {
						// console.log("notification status => ", Notification.permission);
						Notification.requestPermission().then((permission: string) => {
							if (permission === "granted") {
								alert("OTP Sent to Email");
								// Permission granted
							} else {
								// Permission denied
								alert("Alert: Notification permission denied.");
							}
						});
					}
				}
			} else {
				// Display Windows alert
				alert("OTP Sent to Email");
			}

			setOTP("");
			setResendOTPButtonVisible(false);
			setCountDownTime(Date.now() + 2 * 60000);
			setStartTimer(true);
			setVerificationToken(_verificationToken);
		}
	};

	const handleVerifyOTPClick = async () => {
		const payload = {
			otp: OTP,
			check: user.email,
			verification_key: verificationToken
		};
		const isVerified = await api.OTP.verifyOTP(payload);
		if (isVerified === true) {
			alert("Email Id has been verified");
			setActiveStep((prevActiveStep: number) => prevActiveStep + 1);
			setOTP("");
			setOTPVerified(true);
		}
	};

	const handleStatementOfUnderstandingRead = () => {
		setHasReadStatementOfUnderstanding(
			(prevHasReadStatementOfUnderstanding: boolean) => !prevHasReadStatementOfUnderstanding
		);
	};

	const handleClose = useCallback(
		(value: string | null) => {
			setAnchorEl(null);
			if (value) {
				setUser(Object.assign({}, user, { role: value.toUpperCase() }));
			}
		},
		[setUser, user]
	);

	const handleBack = () => {
		setActiveStep((prevActiveStep: number) => prevActiveStep - 1);
	};

	// const openVideoPlayerModal = useCallback(() => {
	// 	setVideoPlayerModalProps(
	// 		Object.assign({}, videoPlayerModalProps, {
	// 			openModal: true
	// 		})
	// 	);
	// }, [videoPlayerModalProps]);

	// const handleRegistrationHelpOpen = useCallback(() => {
	// 	setDocumentDialogProps(
	// 		Object.assign({}, documentDialogProps, {
	// 			...documentDialogProps,
	// 			openDialog: true,
	// 			title: "Registration Help",
	// 			content: registrationHelp
	// 		})
	// 	);
	// }, [documentDialogProps, registrationHelp]);

	const handleStatementOfUnderstandingOpen = useCallback(() => {
		setDocumentDialogProps(
			Object.assign({}, documentDialogProps, {
				...documentDialogProps,
				openDialog: true,
				title: "Statement of Understanding",
				// content: statementOfUnderstanding
				content: statementOfUnderstandingPDF
			})
		);
	}, [documentDialogProps, statementOfUnderstanding]);

	const findUserName = useDebouncedCallback(async (value: string) => {
		if (value !== "") {
			const username = await api.find.findUserName(value);
			if (username === false) {
				setUserNameExists(false);
			} else {
				setUserNameExists(true);
			}
		} else {
			setUserNameExists(false);
		}
	}, 500);

	const findEmail = useDebouncedCallback(async (value: string) => {
		if (value !== "") {
			const params = {
				email: value,
				role: user.role.toUpperCase()
			};
			const email = await api.find.findEmail(params);
			if (email === false) {
				setEmailExists(false);
			} else {
				setEmailExists(true);
			}
		} else {
			setEmailExists(false);
		}
	}, 500);

	const handleConfirmPasswordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setConfirmPassword(value);
	}, []);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let { value } = event.target;
			const { name } = event.target;
			if (name === "user_name") {
				findUserName(value);
				setCheckInvalidUserName(false);

				if (!validateUserName(value)) {
					setCheckInvalidUserName(true);
					//alert("Invalid UserName!");
				}

				setUser(Object.assign({}, user, { [name]: value }));
			} else if (name === "email") {
				// findEmail(value);
				setUser(Object.assign({}, user, { [name]: value }));
			} else if (name === "first_name") {
				const regex = /^[a-zA-Z]+$/;
				if (value.match(regex) || value === "") {
					value = value.slice(0, 1).toUpperCase() + value.slice(1);
					setUser(Object.assign({}, user, { [name]: value }));
				} else if (/\s/g.test(value)) {
					setUser(Object.assign({}, user, { [name]: value }));
				}
			} else if (name === "SSN") {
				if (!pasted) {
					// console.log("Not Pasted");
					value = value.toUpperCase();
					const cleanedValue = value.replaceAll("-", "");
					// Allow letters + numbers
					if (/^[A-Z0-9]*$/.test(cleanedValue)) {
						if (
							!(
								/^[A-Z0-9]{0,3}$/.test(value) ||
								/^[A-Z0-9]{3}-[A-Z0-9]{0,2}$/.test(value) ||
								/^[A-Z0-9]{3}-[A-Z0-9]{2}-[A-Z0-9]{0,4}$/.test(value)
							)
						) {
							value = value.substring(0, value.length - 1);
						} else if (value.length === 3 || value.length === 6) {
							value = value + "-";
						}

						setUser(Object.assign({}, user, { [name]: value }));
					} else {
						event.target.value = "";
						event.target.value = user?.SSN;
					}
				} else {
					// console.log("Pasted");
					setPasted(false);
					value = value.toUpperCase();
					const cleanedValue = value.replaceAll("-", "");
					// if (!(value as any)) {
					if (cleanedValue.length !== 9) {
						alert("Please enter a valid 9 digit SSN or Alt id!");
						// event.target.value = "";
						event.target.value = user?.SSN;
					} else {
						// console.log("Inside Number");
						const ssn = value;
						// console.log("value", value, value.substring(0, 3), value.substring(3, 5), value.substring(5));
						if (ssn.length === 4) {
							value = value.substring(0, 3) + "-" + value.substring(3);
						} else if (ssn.length === 5) {
							value = value.substring(0, 3) + "-" + value.substring(3);
						} else if (ssn.length == 9) {
							value = value.substring(0, 3) + "-" + value.substring(3, 5) + "-" + value.substring(5);
						} else if (ssn.length >= 6 && ssn.length <= 9) {
							value = value.substring(0, 3) + "-" + value.substring(3, 5) + "-" + value.substring(5);
						}

						setUser(Object.assign({}, user, { [name]: value }));
					}
					// }
				}
			} else {
				setUser(Object.assign({}, user, { [name]: value }));
			}
		},
		[findUserName, pasted, setUser, user]
	);

	const handleReEnteredSSNChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let { value } = event.target;

			if (!pasted) {
				// console.log("Not Pasted");
				value = value.toUpperCase();
				const cleanedValue = value.replaceAll("-", "");
				// Allow letters + numbers
				if (/^[A-Z0-9]*$/.test(cleanedValue)) {
					if (
						!(
							/^[A-Z0-9]{0,3}$/.test(value) ||
							/^[A-Z0-9]{3}-[A-Z0-9]{0,2}$/.test(value) ||
							/^[A-Z0-9]{3}-[A-Z0-9]{2}-[A-Z0-9]{0,4}$/.test(value)
						)
					) {
						value = value.substring(0, value.length - 1);
					} else if (value.length === 3 || value.length === 6) {
						value = value + "-";
					}

					setReEnterSSN(value);
				} else {
					event.target.value = "";
					event.target.value = user?.SSN;
				}
			} else {
				setPasted(false);
				if (!(value as any)) {
					if (value.length > 9) {
						event.target.value = "";
						event.target.value = reEnterSSN;
						alert("Please enter a valid 9 digit SSN!");
					} else {
						const ssn = value;
						if (ssn.length === 4) {
							value = value.substring(0, 3) + "-" + value.substring(3);
						} else if (ssn.length === 5) {
							value = value.substring(0, 3) + "-" + value.substring(3);
						} else if (ssn.length == 9) {
							value = value.substring(0, 3) + "-" + value.substring(3, 5) + "-" + value.substring(5);
						} else if (ssn.length >= 6 && ssn.length <= 9) {
							value = value.substring(0, 3) + "-" + value.substring(3, 2) + "-" + value.substring(5);
						}
						setReEnterSSN(value);
					}
				} else {
					let ssnValue = value.replace(/[^A-Z0-9]/g, "").replace(/[^\w\s]/gi, "");
					alert("You have pasted alpha-numeric SSN!");

					if (ssnValue.length > 9) {
						event.target.value = "";
						event.target.value = reEnterSSN as unknown as string;
						alert("Please enter a valid SSN!");
					} else {
						const ssn = ssnValue;

						if (ssn.length === 4) {
							ssnValue = ssnValue.substring(0, 3) + "-" + ssnValue.substring(3);
						} else if (ssn.length === 5) {
							ssnValue = ssnValue.substring(0, 3) + "-" + ssnValue.substring(3);
						} else if (ssn.length == 9) {
							value = value.substring(0, 3) + "-" + value.substring(3, 5) + "-" + value.substring(5);
						} else if (ssn.length >= 6 && ssn.length <= 9) {
							ssnValue =
								ssnValue.substring(0, 3) + "-" + ssnValue.substring(3, 2) + "-" + ssnValue.substr(5);
						}

						setReEnterSSN(ssnValue);
					}
				}
			}
		},
		[pasted, reEnterSSN]
	);

	const getUploadAdministration = useCallback(async () => {
		const uploadAdministration = await api.uploadAdminstration.getUploadAdministration();
		if (uploadAdministration) {
			setStatementOfUnderstanding(
				uploadAdministration.statementOfUnderstanding ? uploadAdministration.statementOfUnderstanding : ""
			);
			setRegistrationHelp(uploadAdministration.registrationHelp ? uploadAdministration.registrationHelp : "");
		}
	}, []);

	const handleSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			if (activeStep === 1) {
				const roleValidation = await handleRoleValidation();
				if (roleValidation === "valid") setActiveStep(activeStep + 1);
			} else if (activeStep === 2) {
				if (OTPVerified) setActiveStep(activeStep + 1);
			} else if (activeStep === 3) {
				const personalDetailsValidation = await handlePersonalDetailsValidation();
				if (personalDetailsValidation === "valid") {
					setActiveStep(activeStep + 1);
					setHasReadStatementOfUnderstanding(false);
				}
			} else if (activeStep === 4) {
				const credentialValidation = await handlecredentialValidation();
				if (credentialValidation === "valid") {
					if (hasReadStatementOfUnderstanding) {
						const { role, date_of_birth } = user;
						const _role = role.toUpperCase();
						const date = date_of_birth ? new Date(date_of_birth) : "";
						const _user = {
							...user,
							SSN: user?.SSN?.replaceAll("-", ""),
							role: user.role.toUpperCase(),
							date_of_birth:
								date != ""
									? date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate()
									: ""
						};
						// console.log("_user", _user);
						try {
							if (_role === ROLES.admin || _role === ROLES.employer) {
								const response = await trackPromise(api.auth.ownerSignUp(_user));
								// console.log("User Response", response);

								if (response) setActiveStep(activeStep + 1);
							} else if (_role === ROLES.employee || _role === ROLES.dependent) {
								const response = await trackPromise(api.auth.memberSignUp(_user));
								// console.log("Member Response", response);

								if (response) setActiveStep(activeStep + 1);
							} else {
								console.log("Invalid Role Selected!");
							}
						} catch (err) {
							console.log("err", err);
							setSignUpDialogProps(
								Object.assign({}, signUpDialogProps, {
									openDialog: true,
									title: "Unsuccessfull Sign Up",
									content: "Unable to sign up. Please try again",
									actions: [
										{
											label: "Close",
											callback: () =>
												setSignUpDialogProps(
													Object.assign({}, signUpDialogProps, {
														openDialog: false
													})
												)
										}
									]
								})
							);
						}
					} else {
						alert("Please Read the Statement of Understanding and accept it.");
					}
				}
			} else if (activeStep === 5) {
				navigate("/login");
			}
		},
		[
			OTPVerified,
			activeStep,
			handlePersonalDetailsValidation,
			handleRoleValidation,
			handlecredentialValidation,
			hasReadStatementOfUnderstanding,
			navigate,
			signUpDialogProps,
			user
		]
	);

	const handleModalClose = () => {
		setDocumentDialogProps(
			Object.assign({}, documentDialogProps, {
				...documentDialogProps,
				openDialog: false
			})
		);
		// setOpen(false);
	};

	// const handleModalOpen = () => {
	// 	// setOpen(true);
	// 	setDocumentDialogProps(
	// 		Object.assign({}, documentDialogProps, {
	// 			...documentDialogProps,
	// 			openDialog: false
	// 		})
	// 	);
	// };
	// const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
	// 	setNumPages(numPages);
	// };

	// useEffect(() => {
	// 	getUploadAdministration();
	// }, [getUploadAdministration]);

	useEffect(() => {
		if (startTimer === true) {
			ref.current = setInterval(() => {
				if (countDownTime) {
					const distance = countDownTime - Date.now();
					const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
					const seconds = Math.floor((distance % (1000 * 60)) / 1000);
					if (minutes === 0 && seconds === 0) {
						setStartTimer(false);
						setResendOTPButtonVisible(true);
						setResentOTPCaptionVisible(true);
					}
					setTimer(
						Object.assign(
							{},
							{
								minutes: String(minutes),
								seconds: seconds < 10 ? "0" + String(seconds) : String(seconds)
							}
						)
					);
				}
			}, 1000);
		}

		return () => clearInterval(ref.current);
	}, [countDownTime, startTimer]);

	// console.log("activeStep", activeStep);

	// console.log("validation status", validation?.user, validation?.status);

	return (
		<div className="sign-up-main-container" id="sign-up-main-container">
			{activeStep === 5 ? (
				<div className="left-section">
					<img className="signup-complete-illustration" src={registrationPageImageB} alt="" />
					<div className="tagline">
						<h3>Here we go!</h3>
						<span>You are now part of nexcalliber</span>
					</div>
				</div>
			) : (
				<div className="left-section">
					<img className="signup-page-illustration" src={registrationPageImageA} alt="" />
					<div className="tagline">
						<h3>Quick and simple way</h3>
						<span>to get registered into our system</span>
					</div>
				</div>
			)}

			<div className="right-page-section">
				{ADMIN_ENV === "DEV" ? (
					<div className="test-portal-login-container" style={{ marginTop: "20px" }}>
						<h2 className="test-portal-login-text">Test Portal</h2>
					</div>
				) : null}
				<img className="nexcaliber-logo" src={nexcaliberLogo} alt="" />
				<div className="header-section">
					<h3>
						Registration <span>Form</span>
					</h3>
					<span>Please Fill up the details accordingly</span>
				</div>

				{activeStep === 5 ? (
					<div className="blank-stepper-container"></div>
				) : (
					<div className="stepper-container">
						{stepperItems.map((data, index) => {
							return (
								<div
									key={index}
									className={`stepper-item ${activeStep === index + 1 && "activeSection"} ${
										index + 1 < activeStep && "completeSection"
									} ${complete === true ? "statusDone" : ""}`}
								>
									{activeStep === index + 1 ? data.stepperIconActive : data.stepperIcon}

									<div className="stepper-text">
										<h3>{data.stepperTextPrimary}</h3>
										<span>{data.stepperTextSecondary}</span>
									</div>
								</div>
							);
						})}
					</div>
				)}

				<div className="wrapper-container">
					{activeStep === 1 ? (
						<RoleSelection
							handleClick={handleClick}
							roleValidation={validation.user.role}
							userRole={user.role}
							anchorEl={anchorEl}
							handleClose={handleClose}
						/>
					) : activeStep === 2 ? (
						<EmailVerification
							handleSubmit={handleSubmit}
							OTPVerified={OTPVerified}
							handleChange={handleChange}
							validationUserId={validation.user.email}
							emailExists={emailExists}
							resendOTPButtonVisible={resendOTPButtonVisible}
							userEmail={user.email}
							resentOTPCaptionVisible={resentOTPCaptionVisible}
							startTimer={startTimer}
							handleSendOTPToEmailClick={handleSendOTPToEmailClick}
							OTP={OTP}
							timer={timer}
							handleOTPChange={handleOTPChange}
							handleVerifyOTPClick={handleVerifyOTPClick}
						/>
					) : activeStep === 3 ? (
						<PersonalDetails
							user={user}
							setUser={setUser}
							handleChange={handleChange}
							handleReEnteredSSNChange={handleReEnteredSSNChange}
							handleSubmit={handleSubmit}
							handleDateChange={handleDateChange}
							validation={validation.user}
							reEnterSSNValidation={validation.reEnterSSN}
							reEnterSSN={reEnterSSN}
							setReEnterSSN={setReEnterSSN}
							setPasted={setPasted}
						/>
					) : activeStep === 4 ? (
						<LoginDetails
							handleSubmit={handleSubmit}
							user={user}
							validation={validation.user}
							handleChange={handleChange}
							hasReadStatementOfUnderstanding={hasReadStatementOfUnderstanding}
							handleStatementOfUnderstandingRead={handleStatementOfUnderstandingRead}
							handleStatementOfUnderstandingOpen={handleStatementOfUnderstandingOpen}
							confirmPasswordValidation={validation.confirmPassword}
							confirmPassword={confirmPassword}
							handleConfirmPasswordChange={handleConfirmPasswordChange}
							checkInvalidUserName={checkInvalidUserName}
							userNameExists={userNameExists}
							handleBack={handleBack}
						/>
					) : (
						<SuccessPage />
						// <>
						// 	<div className="account-successfully-created" id="account-successfully-created">
						// 		<img src={registrationSuccessImage} alt="" />
						// 		<h3>Congratulations</h3>
						// 		<span>Account has been registered successfully</span>
						// 		<button onClick={() => navigate("/login")}>Login</button>
						// 	</div>
						// </>
					)}
				</div>

				<Modal open={documentDialogProps.openDialog} onClose={handleModalClose}>
					<Box
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							width: "80%",
							height: "90%",
							bgcolor: "white",
							p: 4,
							// paddingTop: 1,
							overflow: "hidden"
							// border: "none"
						}}
					>
						<h2 style={{ marginBottom: "10px", fontSize: "24px", color: "#333" }}>
							{documentDialogProps.title}
						</h2>
						{/* PDF Viewer */}
						<div style={{ height: "85%", overflow: "hidden" }}>
							<iframe
								src={documentDialogProps.content}
								style={{ width: "100%", height: "100%" }}
								title="PDF Viewer"
							/>
						</div>
						<Button
							variant="contained"
							onClick={handleModalClose}
							style={{
								marginTop: "20px",
								color: "#ffff",
								backgroundColor: "#8C68E1",
								marginLeft: "auto",
								display: "block"
							}}
						>
							Close
						</Button>
					</Box>
				</Modal>

				{activeStep === 5 ? (
					<div className="blank-container"></div>
				) : (
					<div className="button-container">
						{/* BACK BUTTON */}
						<Button
							className="back-button"
							variant="contained"
							onClick={handleBack}
							style={{
								backgroundColor: "#8C68E1",
								color: "#ffff",
								padding: 10,
								width: 200,
								borderRadius: "50px 0 0 50px"
							}}
							disabled={activeStep === 1 ? true : false}
						>
							<span>
								<FiArrowLeft className="button-icon" style={{ color: "#ffff" }} />
							</span>
							<span className="button-label-with-icon" style={{ color: "#ffff" }}>
								Back
							</span>
						</Button>
						{/* NEXT BUTTON */}
						<Button
							className="submit-next-button"
							variant="contained"
							onClick={handleSubmit}
							style={{
								backgroundColor: "#8C68E1",
								color: "#ffff",
								padding: 10,
								width: 200,
								borderRadius: "0 50px 50px 0"
							}}
						>
							<span className="button-label-with-icon" style={{ color: "#ffff" }}>
								Next
							</span>
							<span>
								<FiArrowRight className="button-icon" style={{ color: "#ffff" }} />
							</span>
						</Button>
					</div>
				)}

				{activeStep === 5 ? null : (
					<div>
						<div className="sign-in-link-container">
							Already a user ? &nbsp;
							<span className="link-container">
								<Link className="sign-in-link" to="/login">
									Sign in
								</Link>
							</span>
						</div>
						{/* <div className="tutorial-link-container">
							<span>Do you need any help?</span>
							<span className="tutorial-link-sub-container">
								<Link className="tutorial-link" to="/forget-user-name-new">
									Click here
								</Link>
								&nbsp; to watch a quick demo
							</span>
						</div> */}
					</div>
				)}

				<div className="copyright-text-container">
					<span>© 2026 nexcaliber, All rights reserved</span>
				</div>
			</div>
		</div>
	);
};

export default SignUp;
